import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import * as Icon from "react-bootstrap-icons";
import { Bars } from "react-loader-spinner";
import styles from "./ApprovalStatusRenderer.module.css";
import { fetchApprovalPageData } from "./ApprovalStatusRenderer.service";
import { Button } from "react-bootstrap";
import { ApprovalStatusRendererProps, ApproverData, updatePopupPosition } from "./ApprovalStatusRenderer.utils";
import { TimeRenderer } from "../../../ClientsGrid/ClientsGrid.utils";
import MessageCellRenderer from "../../../../engagement_letter/EngagementLetterDetailsPage/components/ApprovalStatusRenderer/components/MessageCellRenderer/MessageCellRenderer";
import CygnusGrid from "../../../../../pages/reusable_components/CygnusGrid/CygnusGrid";

const ApprovalStatusRenderer: React.FC<ApprovalStatusRendererProps> = ({
  rowData,
}) => {
  const [showPopup, setShowPopup] = useState<boolean>(false);
  const [popupPosition, setPopupPosition] = useState<{
    top: number;
    left: number;
  }>({ top: 0, left: 0 });
  const [approvalStatusRefreshGrid, setApprovalStatusRefreshGrid] =
    useState<boolean>(false);
  const [approversData, setApproversData] = useState<ApproverData[]>([]);
  const buttonRef = useRef<HTMLDivElement | null>(null);
  const [clientMasterId, setClientMasterId] = useState<number>(0);
  const [approvalStatus, setApprovalStatus] = useState<string>("");
  const popupRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (rowData) {
      setClientMasterId(rowData.ClientMasterId);
      setApprovalStatus(rowData["Approval Status"]);
    }
  }, [rowData]);

  useEffect(() => {
    if (showPopup) {
      fetchApprovalPageData(
        clientMasterId,
        setApproversData,
        setApprovalStatusRefreshGrid
      );
      updatePopupPosition(buttonRef, setPopupPosition);
    }
  }, [clientMasterId, showPopup]);

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setShowPopup(false);
      }
    };

    if (showPopup) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showPopup]);

  const approverDefs = [
    {
      headerName: "Approver Name",
      field: "UserFullName",
      flex: 1,
      minWidth: 130,
      filter: false,
    },
    {
      headerName: "Approver Role",
      field: "UserRole",
      flex: 1,
      minWidth: 130,
      filter: false,
    },
    {
      headerName: "Approval Status",
      field: "ApprovalStatus",
      flex: 1,
      minWidth: 130,
      filter: false,
    },
    {
      headerName: "Reason",
      field: "Message",
      cellRenderer: MessageCellRenderer,
      flex: 2,
      minWidth: 200,
      filter: false,
    },
    {
      headerName: "Updated Date",
      field: "UpdatedDate",
      cellRenderer: TimeRenderer,
      flex: 1,
      minWidth: 160,
      filter: false,
    },
  ];

  return rowData ? (
    <>
      <div className={styles.approvalStatusWrapper}>
        <div>{approvalStatus ? approvalStatus : ""}</div>
        <div ref={buttonRef}>
          <Button
            onClick={() => setShowPopup(!showPopup)}
            className={styles.popopIcon}
          >
            <Icon.PatchQuestion />
          </Button>
        </div>
      </div>

      {showPopup &&
        createPortal(
          <div
            ref={popupRef}
            className={styles.approvalDetailsPopup}
            style={{
              top: popupPosition.top,
              left: popupPosition.left,
            }}
          >
            <div className={`ag-theme-balham  ${styles.popupGrid}`}>
              {!approvalStatusRefreshGrid ? (
                <CygnusGrid
                  columnDefs={approverDefs}
                  fetchData={async () => {
                    // Since this is not paginated, just return everything
                    return { records: approversData, totalCount: approversData.length };
                  }}
                  cachedData={approversData}
                  dataIdentifier={"ApproverId"}
                  rowSelection={"single"}
                />
              ) : (
                <div className={styles.popupLoader}>
                  <Bars
                    height="80"
                    width="80"
                    color="var(--theme1_primary)"
                    ariaLabel="bars-loading"
                    visible={true}
                  />
                </div>
              )}
            </div>
          </div>,
          document.body
        )}
    </>
  ) : null;
};

export default ApprovalStatusRenderer;
